import React, {
  Children,
  cloneElement,
  FC,
  isValidElement,
  useCallback,
  useState,
} from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';
import classes from 'Components/Dialogs/ExceptionWordDialog/ExceptionWordDialog.module.scss';
import { ExceptionWordDto, IExceptionWord } from 'types/exceptionWord';
import add from '../../../assets/images/add.png';
import { FormControl } from '../../ui/FormControl';
import { ReactComponent as Close } from '../../../assets/images/close.svg';
import { ReactComponent as FillClose } from '../../../assets/images/fillClose.svg';
import { ReactComponent as Point } from '../../../assets/images/point.svg';

type FormikValues = {
  id?: number;
  value: string;
};

export enum ExceptionWordDialogType {
  ADD = 'Добавление слова исключения',
  EDIT = 'Редактирование слова исключения',
}

const ButtonLabels: Record<ExceptionWordDialogType, string> = {
  [ExceptionWordDialogType.ADD]: 'Добавить исключение',
  [ExceptionWordDialogType.EDIT]: 'Сохранить',
};

type Props = {
  type?: ExceptionWordDialogType;
  initialValue?: IExceptionWord;
  onSubmit: Function;
  fetchData: () => void;
};

const useStyles = makeStyles({
  root: {
    background: '#FF7221 !important',
    borderRadius: '0 !important',
    color: 'white',
    fontWeight: 550,
    '&:hover, &:focus': {
      background: '#DE6109',
    },
    fontFamily: 'HelveticaNeueCyr',
    // boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
    height: 50,
    padding: '0 64px !important',
  },

  button: {
    background: 'none',
    borderRadius: 0,
    // width: '100%',
    color: '#444A52 !important',
    textTransform: 'none',
    fontWeight: '550 !important',
    '&:hover, &:focus': {
      background: 'none',
    },
    '& p': {
      margin: 0,
    },
    fontFamily: 'HelveticaNeueCyr',
    // boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
    height: 24,
    padding: 0,
    border: 'none',
    boxShadow: 'none',
    textAlign: 'center',
    display: 'flex',
    gap: 8,
  },
});

export const ExceptionWordDialog: FC<Props> = ({
  type = ExceptionWordDialogType.ADD,
  children,
  initialValue,
  onSubmit,
  fetchData,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [focus, setFocus] = useState<boolean>(false);

  const style = useStyles();

  const {
    handleSubmit,
    handleBlur,
    values,
    errors,
    touched,
    setFieldValue,
    resetForm,
  } = useFormik<FormikValues>({
    initialValues: { value: initialValue?.word ?? '' },
    onSubmit: (vals) => {
      const data: Partial<IExceptionWord> = initialValue?.id
        ? {
            id: initialValue.id,
            word: vals.value,
          }
        : { word: vals.value };

      onSubmit(data).then(() => {
        fetchData();
        setOpen(false);
      });
    },
    validationSchema: Yup.object().shape({
      value: Yup.string().required('Обязательно для заполнения'),
    }),
  });

  const handleOpen = useCallback(() => {
    setOpen((prevState) => !prevState);
    resetForm();
  }, [resetForm]);

  return (
    <>
      {Children.count(children) ? (
        Children.map(children, (child) =>
          isValidElement(child)
            ? cloneElement(child, { onClick: handleOpen })
            : child,
        )
      ) : (
        <Button
          variant="outlined"
          onClick={handleOpen}
          className={style.button}
        >
          <img src={add} alt="" />
          <p>Добавить слово исключение</p>
        </Button>
      )}
      <Dialog open={open} onClose={handleOpen} fullWidth maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            <div className={classes.form__dialogTitle}>
              <span>{type}</span>
              {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
              <div
                className={classes.close}
                onMouseOver={() => setFocus(true)}
                onMouseOut={() => setFocus(false)}
                onClick={() => setOpen(false)}
              >
                {focus ? <FillClose /> : <Close />}
              </div>
            </div>
          </DialogTitle>
          <DialogContent>
            <div>
              <div className={classes.dialogContent}>
                <div>
                  <div className={classes.title}>
                    <p>Исключение</p>
                  </div>
                  <Point className={classes.title__point} />
                </div>
              </div>

              <div>
                <FormControl
                  placeholder="Слово"
                  type="text"
                  name="value"
                  value={values.value}
                  onChange={(e) => setFieldValue('value', e.target.value)}
                  error={Boolean(errors?.value && touched?.value)}
                  errorMessage={errors?.value}
                  onBlur={handleBlur}
                  // onFocus={setFieldTouched('email', false)}
                />
              </div>
            </div>
          </DialogContent>
          <div className={classes.form__row__button}>
            <Button className={style.root} type="submit" color="primary">
              {ButtonLabels[type]}
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
};
